import { inject, Injectable } from '@angular/core'
import { _assert } from '@naturalcycles/js-lib'
import {
  CancelSubscriptionReason,
  isRollingTrialKey,
  Product,
  ProductKey,
  SubscriptionRetryInput,
} from '@naturalcycles/shared'
import { TranslateService } from '@ngx-translate/core'
import { CancellationReasonsModalComponent } from '@src/app/components/modals/cancel-subscription/cancellation-reasons-modal.component.js'
import { ConfirmCancelRollingTrialSubscriptionModalComponent } from '@src/app/components/modals/confirm-cancel-rolling-trial-subscription/confirm-cancel-rolling-trial-subscription-modal.component.js'
import { ConfirmCancelSubscriptionModalComponent } from '@src/app/components/modals/confirm-cancel-subscription/confirm-cancel-subscription-modal.component.js'
import { SimpleModalComponent } from '@src/app/components/modals/simple-modal.component.js'
import { api } from '@src/app/core/services/api.service.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { ModalService } from './modal.service.js'

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private store = inject(SignalStore)
  private modalService = inject(ModalService)
  private translateService = inject(TranslateService)

  async updateState(): Promise<void> {
    await api.put(`subscriptions`)
  }

  async confirmCancelFutureSubscription(
    firstBillingDate: string,
    futurePlan: ProductKey,
  ): Promise<boolean> {
    let confirmCancel = false
    await this.modalService.show({
      component: SimpleModalComponent,
      modalName: 'CancelFutureSubscriptionModal',
      titleKey: 'txt-cancel-upcoming-sub',
      api: {
        messageKey: 'alert-confirm-cancel-future-subscription-body',
        messageParams: {
          firstBillingDate,
          futurePlan: this.translateService
            .instant(`account-subscription--${futurePlan}`)
            .toLowerCase(),
        },
      },
      controls: [
        { key: 'btn-no-I-dont' },
        {
          key: 'btn-yesplease',
          action: () => {
            confirmCancel = true
          },
        },
      ],
    })
    return confirmCancel
  }

  async confirmCancelSubscription(plan?: ProductKey): Promise<boolean> {
    let modalName = 'ConfirmCancelSubscriptionModal'
    let modalComponent = ConfirmCancelSubscriptionModalComponent
    const isCancellingRollingTrial = !!plan && isRollingTrialKey(plan)
    if (isCancellingRollingTrial) {
      modalName = 'ConfirmCancelRollingTrialSubscriptionModal'
      modalComponent = ConfirmCancelRollingTrialSubscriptionModalComponent
    }

    let confirmCancel = false
    await this.modalService.show({
      component: modalComponent,
      modalName,
      controls: [
        { key: 'btn-no-I-dont' },
        {
          key: 'btn-yesplease',
          action: () => {
            confirmCancel = true
          },
        },
      ],
    })
    return confirmCancel
  }

  async askCancellationReason(): Promise<CancelSubscriptionReason | undefined> {
    let cancellationReason: CancelSubscriptionReason | undefined
    await this.modalService.show({
      component: CancellationReasonsModalComponent,
      skipControls: true,
      api: {
        select: (reason: CancelSubscriptionReason) => {
          cancellationReason = reason
          this.modalService.remove()
        },
      },
    })
    return cancellationReason
  }

  async retryCharge(input: SubscriptionRetryInput): Promise<void> {
    await api.post(`subscriptions/retry/${input.subscriptionId}`, {
      json: input,
    })
  }

  public async confirmUpdateFutureSubscription(
    firstBillingDate: string,
    futurePlan: ProductKey,
    currentPlan?: ProductKey,
  ): Promise<boolean> {
    let confirmUpdate = false
    await this.modalService.show({
      component: SimpleModalComponent,
      modalName: 'UpdateFutureSubscriptionModal',
      titleKey: 'txt-update-upcoming-sub',
      api: {
        messageKey: 'alert-confirm-update-future-subscription-body',
        messageParams: {
          firstBillingDate,
          currentPlan: currentPlan
            ? this.translateService.instant(`account-subscription--${currentPlan}`).toLowerCase()
            : '',
          futurePlan: this.translateService
            .instant(`account-subscription--${futurePlan}`)
            .toLowerCase(),
        },
      },
      controls: [
        { key: 'btn-no-I-dont' },
        {
          key: 'btn-yesplease',
          action: () => {
            confirmUpdate = true
          },
        },
      ],
    })
    return confirmUpdate
  }

  public getDurationUnit(subscription: Product): string {
    switch (subscription.key) {
      case ProductKey.MONTH: {
        return this.translateService.instant(`month`)
      }
      case ProductKey.YEAR: {
        return this.translateService.instant(`txt-year`)
      }
      default: {
        return `${subscription.duration}-${subscription.durationUnit}`
      }
    }
  }

  public getRenewalProductForRollingTrial(plan: ProductKey): Product | undefined {
    if (!isRollingTrialKey(plan)) return undefined
    // For now we only support the yearly plan as the renewal plan for the rolling trial
    // FIXME: https://naturalcycles.atlassian.net/browse/DEV-20177?atlOrigin=eyJpIjoiYzJmYzMxNmExODQxNGY1YWE5ZDU3YzM1N2RmMjg5MTUiLCJwIjoiaiJ9
    const renewalProduct = this.store.$product().items.find(i => i.key === ProductKey.YEAR)

    _assert(
      renewalProduct,
      `No corresponding renewal plan found for rolling trial with key ${plan}`,
    )

    return renewalProduct
  }
}
